import _, { assign, get, isEqual, set } from 'lodash';
import moment from 'moment';

import { type IServerSideGetRowsRequest } from 'ag-grid-community';

import { type NamedSelect } from 'common/soql_builder/select';
import { SoQLType } from 'common/types/soql';
import { type ViewColumn } from 'common/types/viewColumn';

import { type ColumnAggregation } from '../dataProviders/types';
import { getDisplayableColumns } from '../dataProviders/MetadataProvider';
import InlineDataProvider from '../dataProviders/InlineDataProvider';
import SocrataViewDataProvider from '../dataProviders/SocrataViewDataProvider';
import { getSoqlMetaBundle } from '../dataProviders/getSoqlMetaBundle';
import { type CustomSortConfig, type HierarchyColumnConfig, type OrderConfig, type Vif } from '../vif';
import { type TableActionsContextField } from '../views/agGridReact/TableActionsButton/types';
import {
  isTableActionConfigured,
  tableActionValueKey,
} from '../views/agGridReact/helpers/tableActionsHelper';
import { NULL_GROUPING } from '../views/agGridReact/Constants';
import { SERIES_TYPE_AG_GRID_TABLE } from '../views/SvgConstants';

import { mapSoqlRowsResponseToTable, whereClauseFilteringOwnColumn } from './SoqlHelpers';
import { checkForColumnMismatch, getColumnUpdates } from './TableDataColumnHelpers';
import { getDatasetRowCount } from './TableDataRowCountHelpers';
import {
  createMeasureAliasMap,
  createSelectAliasMap,
  isDoingGrouping,
  tableDataQuery,
  unaliasRowData,
} from './TableDataQueryHelpers';
import { getDefaultSortOrder } from './TableDataSortHelpers';
import { MAX_COLUMN_COUNT } from './TableHelpers';
import { getCurrentHierarchyIndex } from './VifHelpers';
import {
  getDatasetUid,
  isTableActionFeatureEnabledForReports,
  isTableAdvancedTotalingEnabled,
} from './VifSelectors';
import { isPivotInAgRequest } from './PivotHelpers';
import {
  customLabelsPath,
  hasAggregationPath,
  rowGroupCountPath,
  showGrandTotalPath,
  showSubTotalPath,
} from './AgTableObjectPathHelpers';

/**
 * Functions for setting queries for different types of data providers
 */

/**
 * Utilizes the InlineDataProvider, ie: summary tables, SOQL query editor.
 * @param {Object} vifWithInlineData - Vif of the given table.
 * @param {Number} datasetRowCount - Row count of the table, generated from the corresponding get_____RowCount() function.
 * @param {Number} startIndex - Index of first row (offset).
 * @param {Number} pageSize - Number of items in page (not necessarily in rows[]).
 * @param {Boolean} pagingEnabled - If paging is enabled for the given table.
 */
export function setInlineDataQuery(
  vifWithInlineData: Vif,
  datasetRowCount: number | null,
  startIndex: number,
  pageSize: number,
  pagingEnabled: boolean,
) {
  const inlineDataProvider = new InlineDataProvider(vifWithInlineData);
  // Fetched data returns number of rows per pageSize from startIndex
  // All rows are fetched if paging is disabled
  const newState = {
    busy: false,
    datasetRowCount: datasetRowCount,
    error: false,
    fetchedData: {
      columns: inlineDataProvider.getColumns(),
      order: null,
      pageSize: pagingEnabled ? pageSize : inlineDataProvider.getRowCount(),
      rows: pagingEnabled
        ? inlineDataProvider.getTableRows(startIndex, pageSize)
        : inlineDataProvider.getRows(),
      rowIds: pagingEnabled
        ? inlineDataProvider.getTableRowIds(startIndex, pageSize)
        : inlineDataProvider.getRowIds(),
      startIndex: pagingEnabled ? startIndex : inlineDataProvider.getStartIndex(),
    },
    vif: vifWithInlineData,
  };

  return Promise.resolve(newState);
}

/**
 * Utilizes the SoqlDataProvider, but expects raw Soql, ie: notifications and alerts, which can accept a user input rawSoqlQuery to generate an alert.
 * @param {Object} vifForDataQuery - Vif of the given table.
 * @param {Number} datasetRowCount - Row count of the table, generated from the corresponding get_____RowCount() function.
 * @param {Number} startIndex - Index of first row (offset).
 * @param {Number} pageSize - Number of items in page (not necessarily in rows[]).
 * @param {Object} order - Sort order. Only one element supported.
 * @param {string} order.columnName - Name of the column to sort by.
 * @param {Boolean} order.ascending - If the order is ascending or not (false = descending).
 */

export async function setRawSoqlDataQuery(
  vifForDataQuery: Vif,
  datasetRowCount: number | null,
  startIndex: number,
  pageSize: number,
  order?: OrderConfig[],
) {
  const { soqlDataProvider, metadataProvider } = getSoqlMetaBundle(vifForDataQuery);
  const soqlQuery = get(vifForDataQuery, 'series[0].dataSource.rawSoqlQuery');
  const columns = await metadataProvider.getOutputColumnsForQuery(soqlQuery);
  const columnNames = columns.map((column: ViewColumn) => column.fieldName);

  let soqlQueryWithPaginationAndSorting = `${soqlQuery} |> select ${columnNames.join(',')}`;

  if (!_.isEmpty(order)) {
    const orderByColumnName = get(order, '[0].columnName');
    const orderDirection = get(order, '[0].ascending') ? 'asc' : 'desc';

    soqlQueryWithPaginationAndSorting =
      soqlQueryWithPaginationAndSorting + ` order by ${orderByColumnName} ${orderDirection}`;
  }

  soqlQueryWithPaginationAndSorting =
    soqlQueryWithPaginationAndSorting + ` limit ${pageSize} offset ${startIndex}`;

  const { rows } = mapSoqlRowsResponseToTable(
    columnNames,
    await soqlDataProvider.rawQuery(soqlQueryWithPaginationAndSorting),
  );

  return {
    busy: false,
    datasetRowCount: datasetRowCount,
    error: false,
    fetchedData: {
      columns,
      order,
      pageSize,
      rows,
      rowIds: [],
      startIndex,
    },
    vif: vifForDataQuery,
  };
}

/**
 * Utilizes the SoqlDataProvider. used in: summary tables, AX table, Viz Canvas table, grid view
 * @param {Object} vifForDataQuery - Vif of the given table.
 * @param {Number} datasetRowCount - Row count of the table, generated from the corresponding get_____RowCount() function.
 * @param {Number} startIndex - Index of first row (offset).
 * @param {Number} pageSize - Number of items in page (not necessarily in rows[]).
 * @param {Object} order - Sort order. Only one element supported.
 * @param {string} order.columnName - Name of the column to sort by.
 * @param {Boolean} order.ascending - If the order is ascending or not (false = descending).
 * @param {Boolean} shouldDisplayFilterBar - Determines if the filter bar should be displayed on the table
 */
export async function setSoqlDataQuery(
  vifForDataQuery: Vif,
  datasetRowCount: number,
  startIndex: number,
  pageSize: number,
  order: OrderConfig[],
  shouldDisplayFilterBar: boolean,
  userChangedOrder: boolean,
  { doNotMutateVif = false } = {},
) {
  const { soqlDataProvider, metadataProvider } = getSoqlMetaBundle(vifForDataQuery);
  const datasetUid = getDatasetUid(vifForDataQuery);
  const whereClauseComponents = whereClauseFilteringOwnColumn(vifForDataQuery, 0);
  const datasetMetadata = await metadataProvider.getDatasetMetadata();

  // New columns will only be set if the table is generated from the authoring experience interactive table visualization. If the user has selected a subset of columns, only use the subset, rather than the full list of available columns.
  let newColumns = get(vifForDataQuery, 'series[0].dataSource.dimension.columns');
  const allDatasetColumns = getDisplayableColumns(datasetMetadata);
  const allDatasetColumnsCount = allDatasetColumns.length;
  const hasNewColumns = !!newColumns;

  const updatedVif = _.cloneDeep(vifForDataQuery);

  // If we have newColumns (set in a Table Viz) this will run
  // If it returns true, this function will throw an error and return early
  checkForColumnMismatch(allDatasetColumns, newColumns);

  if (hasNewColumns) {
    newColumns = getColumnUpdates(allDatasetColumns, newColumns);
  }

  const displayableColumns = newColumns ? newColumns : allDatasetColumns.slice(0, MAX_COLUMN_COUNT);
  if (doNotMutateVif) {
    set(updatedVif, 'series[0].dataSource.dimension.columns', displayableColumns);
  } else {
    set(vifForDataQuery, 'series[0].dataSource.dimension.columns', displayableColumns);
  }

  if (_.isUndefined(order) || _.isEmpty(order)) {
    let sortOrder: OrderConfig[] | [] = [];
    // If the user has manually cleared the sort, fall back to sorting by default sort or no sort. Otherwise we'll just
    // call out to dsmapi again and reset the sort to the default one
    // if this is first load and the sort is undefined or empty, first check for a default sort or fallback to no sort.
    if (!userChangedOrder) {
      sortOrder = (await getDefaultSortOrder(datasetMetadata, datasetUid)) || [];
    }

    order = sortOrder;
    // Update order in vifForDataQuery so we can visually indicate which column the table is
    // being sorted by
    if (doNotMutateVif) {
      set(updatedVif, 'configuration.order', order);
    } else {
      set(vifForDataQuery, 'configuration.order', order);
    }
  }
  const tableType = get(vifForDataQuery, 'series[0].type');

  const soqlDataPromise = soqlDataProvider.getTableData(
    _.map(displayableColumns, 'fieldName'),
    order,
    startIndex,
    pageSize,
    whereClauseComponents,
    tableType,
  );

  const filterableColumnsPromise = shouldDisplayFilterBar
    ? metadataProvider.getDisplayableFilterableColumns({
        datasetMetadata: Promise.resolve(datasetMetadata),
      })
    : null;

  const [soqlData, filterableColumns] = await Promise.all([soqlDataPromise, filterableColumnsPromise]);
  const newState = {
    busy: false,
    datasetRowCount: datasetRowCount,
    error: false,
    fetchedData: {
      rows: soqlData.rows,
      rowIds: soqlData.rowIds,
      columns: displayableColumns,
      filterableColumns,
      startIndex,
      pageSize,
      order,
      whereClauseComponents,
      allDatasetColumnsCount,
      hasNewColumns,
    },
    vif: doNotMutateVif ? updatedVif : vifForDataQuery,
  };

  return newState;
}

/**
 * Synthetic aggregation type used for table-action context fields. The query
 * builder recognises this in the {@link ColumnAggregation} list and emits
 * `CASE WHEN COUNT(DISTINCT col) = 1 THEN MAX(col) ELSE NULL END` so the
 * group row carries a uniform value (or `null` when children disagree).
 */
const TABLE_ACTION_UNIFORM_AGG_TYPE = 'tableActionUniformValue';

const buildUniformAggregationSoql = (fieldName: string) =>
  `CASE WHEN count(distinct \`${fieldName}\`) = 1 THEN max(\`${fieldName}\`) ELSE NULL END`;

/**
 * Builds an augmented request + aggregation list that injects a
 * "uniform value or null" aggregate for every required action field that
 * isn't already in the SELECT. Returns `null` when no augmentation is
 * needed (leaf level, no mappings, or every field already covered).
 *
 * The aggregations are registered under namespaced ids so the agg alias map
 * (keyed off `valueCol.id`) routes the SoQL result back onto the row under
 * `__tableActionValue__<field>` instead of the column's own name — that
 * keeps the parent group cell's visible value untouched.
 */
export const buildTableActionsAugmentation = (
  agRequest: IServerSideGetRowsRequest,
  vif: Vif,
): { request: IServerSideGetRowsRequest; aggregations: ColumnAggregation[] } | null => {
  const mappings: TableActionsContextField[] = get(vif, 'configuration.contextFieldMappings', []);
  const groupKeys = agRequest.groupKeys ?? [];
  const rowGroupCols = agRequest.rowGroupCols ?? [];

  if (
    !isTableActionFeatureEnabledForReports() ||
    !isTableActionConfigured(vif.configuration) ||
    !isDoingGrouping(rowGroupCols, groupKeys) ||
    mappings.length === 0
  ) {
    return null;
  }

  // Skip only the column being grouped on at THIS level (it's already in the
  // SELECT as the group key). Columns grouped on at deeper levels are still
  // worth aggregating — their uniformity tells us whether the current group
  // resolves to a single value of the deeper field.
  const currentLevelGroupingColId = rowGroupCols[groupKeys.length]?.id;
  const existingValueColIds = new Set((agRequest.valueCols ?? []).map((c) => c.id));

  const newFieldNames = [
    ...new Set(
      mappings
        .map((m) => m.selectedColumn)
        .filter((field) => !!field && field !== currentLevelGroupingColId && !existingValueColIds.has(field)),
    ),
  ];

  if (newFieldNames.length === 0) {
    return null;
  }

  const aggregations: ColumnAggregation[] = newFieldNames.map((fieldName) => ({
    fieldName: tableActionValueKey(fieldName),
    aggregationType: TABLE_ACTION_UNIFORM_AGG_TYPE,
    dataTypeName: SoQLType.SoQLTextT,
    format: {},
    soqlExpression: buildUniformAggregationSoql(fieldName),
  }));

  const syntheticValueCols = newFieldNames.map((fieldName) => ({
    id: tableActionValueKey(fieldName),
    displayName: tableActionValueKey(fieldName),
    field: tableActionValueKey(fieldName),
    aggFunc: TABLE_ACTION_UNIFORM_AGG_TYPE,
  }));

  return {
    request: {
      ...agRequest,
      valueCols: [...(agRequest.valueCols ?? []), ...syntheticValueCols],
    },
    aggregations,
  };
};

/**
 * Utilizes the Soql Query Builder or SoqlDataProvider depending on if there are hierarchies, ie: Ag Grid tables.
 */
export async function setAgSoqlDataQuery({
  agRequest,
  order,
  pageSize,
  previousOrder,
  searchString,
  shouldFetchAggregationSuggestions,
  startIndex,
  vifForDataQuery,
}: {
  /** Data request info made by Ag Grid. Needs to be passed through to tableDataQuery. */
  agRequest: IServerSideGetRowsRequest;
  order?: OrderConfig[];
  /** Number of items in page (not necessarily in rows[]). */
  pageSize: number;
  previousOrder?: OrderConfig[];
  searchString?: string;
  shouldFetchAggregationSuggestions: boolean;
  startIndex: number;
  vifForDataQuery: Vif;
  /** Index of the first row to be requested (aka the offset) */
}) {
  const { soqlDataProvider, metadataProvider } = getSoqlMetaBundle(vifForDataQuery);
  const whereClause = whereClauseFilteringOwnColumn(vifForDataQuery, 0);
  const datasetMetadata = await metadataProvider.getDatasetMetadata();
  const nonStandardAggregations = shouldFetchAggregationSuggestions
    ? await metadataProvider.getAggregationSuggestions()
    : null;

  const displayableColumns = getDisplayableColumns(datasetMetadata);

  const updatedVif = _.cloneDeep(vifForDataQuery);

  // If the order in the VIF is undefined, we need to find a column to sort the table by
  // TODO: There is a behavior difference between this and setSoqlDataQuery
  // setSoqlDataQuery tracks "userChangedOrder", and if that is true and order by is empty
  // it issues the query with no order (bad for pagination) or with order by :id, whereas this returns to the original default order
  // (from a users perspective, they've toggled off the order and then it regenerates)
  if ((_.isUndefined(order) || _.isEmpty(order)) && !isPivotInAgRequest(agRequest)) {
    const defaultSortOrder =
      (await getDefaultSortOrder(datasetMetadata, getDatasetUid(vifForDataQuery), previousOrder)) || [];

    order = defaultSortOrder;
    // Update order in vifForDataQuery so we can visually indicate which column the table is
    // being sorted by

    // for soql-views we generate basically an all-column sort,
    // which does a best-possible job of creating a unique order for pagination
    // but looks yucky to display, so we won't
    if (!_.has(datasetMetadata, 'queryString')) {
      set(updatedVif, 'configuration.order', order);
    }
  }

  const hierarchyIndex = getCurrentHierarchyIndex(updatedVif);
  const customSortConfig = get(
    updatedVif,
    `series[0].dataSource.hierarchies[${hierarchyIndex}].columnSortConfig`,
  );
  const hierarchyConfigs: HierarchyColumnConfig[] = get(
    updatedVif,
    `series[0].dataSource.hierarchies[${hierarchyIndex}].columnConfigurations`,
  );

  // when the SSR is asking for a grouped level and the report has
  // table actions configured, request per-group "uniform value or null"
  // aggregates for every non-grouping required field. The aggregate lands on
  // the group row's `data`, so the link renderer can resolve a value (or know
  // children disagreed) without expanding the group.
  const tableActionsAugmentation = buildTableActionsAugmentation(agRequest, vifForDataQuery);
  const effectiveRequest = tableActionsAugmentation?.request ?? agRequest;
  const effectiveNonStandardAggregations = tableActionsAugmentation
    ? [...(nonStandardAggregations ?? []), ...tableActionsAugmentation.aggregations]
    : nonStandardAggregations;

  const tableData: any = await soqlDataProvider.invokeSoqlQuery(tableDataQuery, [
    displayableColumns,
    whereClause,
    effectiveRequest,
    pageSize,
    startIndex,
    order,
    effectiveNonStandardAggregations,
    searchString,
    customSortConfig,
    hierarchyConfigs,
  ]);

  const selectAliases = createSelectAliasMap(effectiveRequest, displayableColumns);
  const aggAliases = createMeasureAliasMap(effectiveRequest);
  const unaliasedTableData = unaliasRowData(tableData, { ...aggAliases, ..._.invert(selectAliases) });

  const soqlData = mapSoqlRowsResponseToTable(
    displayableColumns,
    unaliasedTableData,
    SERIES_TYPE_AG_GRID_TABLE,
  );

  const rowCount = await getDatasetRowCount(
    vifForDataQuery,
    displayableColumns,
    whereClause,
    agRequest,
    searchString,
  );

  return {
    vif: updatedVif,
    rows: soqlData.rows,
    datasetRowCount: rowCount,
  };
}

/**
 * Utilizes the SocrataViewDataProvider, ie: the /data view, Socrata Data Player embeds.
 * @param {Object} vifWithView - Vif of the given table.
 * @param {Number} datasetRowCount - Row count of the table, generated from the corresponding get_____RowCount() function.
 */
export function setSocrataViewDataQuery(vifWithView: Vif, datasetRowCount: number | null) {
  const dataProviderConfig = {
    domain: get(vifWithView, 'series[0].dataSource.domain'),
    inDatasetSearchQueryTimeoutSeconds: get(
      window,
      'blist.configuration.inDatasetSearchQueryTimeoutSeconds',
      30,
    ),
  };
  const socrataViewDataProvider = new SocrataViewDataProvider(dataProviderConfig);
  const offset = get(vifWithView, 'series[0].dataSource.offset', 0);
  const limit = get(vifWithView, 'series[0].dataSource.limit', 5000);
  const queryConfig = {
    currentView: get(vifWithView, 'series[0].dataSource.currentView', {}),
    savedView: get(vifWithView, 'series[0].dataSource.savedView', null),
    parentView: get(vifWithView, 'series[0].dataSource.parentView', null),
    offset,
    limit,
  };

  const currentQuery = get(queryConfig, 'currentView.query', {});
  const currentQueryHasGroupBys = get(currentQuery, 'groupBys', []).length > 0;
  const currentQueryHasOrderBys = get(currentQuery, 'orderBys', []).length > 0;
  // EN-29606 - Grid view incorrectly believing view is not the saved view
  //
  // In the case where we are querying a view that is already saved, we should
  // be doing a 'select *' query instead of trying to recreate the actual SoQL
  // based on the query defined in the view.
  //
  // The code below that removes invalid order by clauses based on the presence
  // or absence of grouping was incorrectly assigning an empty orderBys array
  // to the view when the saved version did not even have an orderBys property.
  //
  // This caused downstream code to believe that the current view was not also
  // the saved view, which caused it to try to reconstruct the SoQL query that
  // the query defined, even though the backend was already generating that
  // SoQL itself.

  if (currentQueryHasGroupBys && currentQueryHasOrderBys) {
    // OrderBys that are not in the groupBys or are not being aggregated
    // are invalid if groupBys or aggregations exist.
    const aggregationColumns = get(queryConfig, 'currentView.columns', [])
      .filter((column: ViewColumn) => {
        return (
          !_.isUndefined(get(column, 'format.grouping_aggregate')) ||
          !_.isUndefined(get(column, 'format.group_function'))
        );
      })
      .map((column: ViewColumn) => {
        return {
          columnId: column.id,
          type: 'column',
        };
      });
    const groupBysAndAggregations = currentQuery.groupBys.concat(aggregationColumns);
    const validOrderBys = _.intersectionWith(
      currentQuery.orderBys,
      groupBysAndAggregations,
      function (ob: any, gb: any) {
        return ob.expression.columnId == gb.columnId;
      },
    );

    // Remove any orderBys that are invalid (that are not in groupBys and are
    // not being aggregated).
    currentQuery.orderBys = validOrderBys;
  }

  return socrataViewDataProvider.query(queryConfig).then(function (response: {
    columns: any;
    rows: any;
    rowIds: any;
  }) {
    const newState = {
      busy: false,
      datasetRowCount: datasetRowCount,
      error: false,
      fetchedData: {
        columns: socrataViewDataProvider.getColumnsForQuery(queryConfig),
        order: get(vifWithView, 'configuration.order', null),
        pageSize: limit,
        rows: response.rows,
        rowIds: response.rowIds,
        startIndex: offset,
      },
      vif: vifWithView,
    };
    return newState;
  });
}

export function removeNullGroupKeys(groupKeys: (string | null)[]) {
  return groupKeys.map((key) => (key === NULL_GROUPING || key == undefined ? null : key));
}

export function buildCasesParts(
  selectParts: NamedSelect[],
  customSortConfig: CustomSortConfig | undefined,
  orderConfig: OrderConfig[] | undefined,
): { [key: string]: string } {
  const caseClause = _(selectParts)
    .map((part) => {
      const fieldName = get(part, 'expr.fieldName');
      const dataTypeName = get(part, 'expr.dataTypeName') as SoQLType;
      const alias = get(part, 'name');
      const values = customSortConfig?.[fieldName] ?? [];
      if (!fieldName || !alias || !orderConfig) {
        return [];
      }

      const order = orderConfig.find((o) => o.columnName === fieldName);

      if (!order || order.sortToFollow !== 'customSort') {
        return [];
      }

      const generatedValues = values.map((value: any, index) => {
        let formattedValue;

        if (isEqual(value, {})) {
          formattedValue = 'NULL';
        } else {
          switch (dataTypeName) {
            case SoQLType.SoQLNumberT:
              formattedValue = value;
              break;

            case SoQLType.SoQLBooleanT:
            case SoQLType.SoQLBooleanAltT:
              formattedValue = `${value.toString().toLowerCase()}`;
              break;

            case SoQLType.SoQLFloatingTimestampT:
            case SoQLType.SoQLFloatingTimestampAltT: {
              const momentValue = moment(value);
              if (momentValue.isValid()) {
                const formattedTimestamp = momentValue.format('YYYY-MM-DDTHH:mm:ss.SSS');
                formattedValue = `'${formattedTimestamp}'::floating_timestamp`;
              } else {
                throw new Error(`Invalid date value: ${value}`);
              }
              break;
            }

            case SoQLType.SoQLURLT: {
              const url = value.url;
              const escapedUrl = url.toString().replace(/'/g, "''");
              formattedValue = `'${escapedUrl}'`;
              break;
            }

            case SoQLType.SoQLTextT:
            default: {
              const escaped = value.toString().replace(/'/g, "''");
              formattedValue = `'${escaped}'`;
            }
          }
        }

        const operator = isEqual(value, {}) ? ' IS ' : '=';

        return `${alias}${operator}${formattedValue},${index}`;
      });
      return { [fieldName]: generatedValues.join(', ') };
    })
    .flatten()
    .value();

  return assign({}, ...caseClause);
}

export const tableHasAggregation = (columnConfigurations: HierarchyColumnConfig[]) => {
  return columnConfigurations?.some((col) => !!col.aggregation);
};

export const setHasAggregation = (vif: Vif, hierarchyIndex: number, hasAggregation: boolean) => {
  set(vif, hasAggregationPath(hierarchyIndex), hasAggregation);

  // legacy behavior: when advanced totaling is off, dropping aggregations
  // clears the totals/subtotals checkboxes and their labels. once advanced
  // totaling is permanently on, this branch and the whole helper can go away.
  if (!hasAggregation && !isTableAdvancedTotalingEnabled()) {
    set(vif, showGrandTotalPath(hierarchyIndex), false);
    set(vif, showSubTotalPath(hierarchyIndex), false);

    set(vif, `${customLabelsPath(hierarchyIndex)}.totals`, '');
    set(vif, `${customLabelsPath(hierarchyIndex)}.subtotals`, '');
  }
};

export const setRowGroupCount = (vif: Vif, hierarchyIndex: number, rowGroupCount: number) => {
  set(vif, rowGroupCountPath(hierarchyIndex), rowGroupCount);

  // legacy behavior: see setHasAggregation above. with advanced totaling on,
  // the subtotal checkbox and its label persist across row-group changes.
  if (rowGroupCount <= 0 && !isTableAdvancedTotalingEnabled()) {
    set(vif, showSubTotalPath(hierarchyIndex), false);
    set(vif, `${customLabelsPath(hierarchyIndex)}.subtotals`, '');
  }
};

/**
 * Checks if the current hierarchy configuration contains at least one
 * aggregation on a numeric column.
 */
export const checkHasNumberAggregation = (
  columns: ViewColumn[],
  columnConfigs: HierarchyColumnConfig[],
): boolean => {
  if (!columns.length || !columnConfigs.length) {
    return false;
  }

  const aggregationColumnNames = new Set(
    columnConfigs.filter((config) => config.aggregation !== null).map((config) => config.columnName),
  );

  return columns.some(
    (column) => column.dataTypeName === SoQLType.SoQLNumberT && aggregationColumnNames.has(column.fieldName),
  );
};
